import Tag from '@payaca/components/tag/Tag';
import { PublicTimelogLinkedEntity } from '@payaca/types/timelogs';
import { FC, useMemo, useState, type JSX } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import ScheduledEventReadDrawer from '../scheduledEventReadDrawer/ScheduledEventReadDrawer';
import TaskReadCompleteDrawer from '../taskReadCompleteDrawer/TaskReadCompleteDrawer';
import './TimelogLinkedEntity.sass';

type Props = {
  entity: PublicTimelogLinkedEntity;
};

const TimelogLinkedEntity: FC<Props> = ({ entity }): JSX.Element | null => {
  const history = useHistory();

  const [showScheduledEventDrawer, setShowScheduledEventDrawer] =
    useState(false);
  const [showTaskDrawer, setShowTaskDrawer] = useState(false);

  const linkTo = useMemo(() => {
    switch (entity.entityType) {
      case 'deal':
        return `/projects/${entity.entityPublicId}`;
      case 'customer':
        return `/customers/${entity.entityPublicId}`;
    }
  }, [entity]);

  const clickAction = useMemo(() => {
    switch (entity.entityType) {
      case 'task':
        return () => setShowTaskDrawer(true);
      case 'scheduledEvent':
        return () => setShowScheduledEventDrawer(true);
    }
  }, [entity]);

  const readableEntityType = useMemo(() => {
    switch (entity.entityType) {
      case 'scheduledEvent':
        return 'event';
      case 'deal':
        return 'project';
      default:
        return entity.entityType;
    }
  }, [entity]);

  const tagElement = useMemo(() => {
    return (
      <Tag showTagIcon={false} isRemovable={false} colour="#edf2f7">
        {readableEntityType}: {entity.entityReadableIdentifier}
      </Tag>
    );
  }, [readableEntityType, entity]);

  return (
    <>
      <div className="timelog-linked-entity">
        {linkTo && (
          <Link to={linkTo} target="_blank" rel="noopener noreferrer">
            {tagElement}
          </Link>
        )}

        {!linkTo && (
          <div
            className={clickAction ? 'clickable' : ''}
            role={clickAction ? 'button' : ''}
            onClick={clickAction}
          >
            {tagElement}
          </div>
        )}
      </div>
      <ScheduledEventReadDrawer
        isOpen={showScheduledEventDrawer}
        onClose={() => setShowScheduledEventDrawer(false)}
        scheduledEventId={+entity.entityPublicId}
        zIndexLevel={2}
      />
      <TaskReadCompleteDrawer
        isOpen={showTaskDrawer}
        onClose={() => setShowTaskDrawer(false)}
        taskId={+entity.entityPublicId}
        zIndexLevel={2}
      />
    </>
  );
};

export default TimelogLinkedEntity;
